import React from "react";
import { Box, Tooltip, Typography } from "@material-ui/core";
import { Airport, TripDetails } from "redmond";
import { AirlineIcon, formatInterval, removeTimezone, SanitizedHtml } from "halifax";
import dayjs from "dayjs";
import { uniqBy } from "lodash-es";
import { getSliceIndex } from "../../../../../../../../utils/flights";
import { getReviewCardHeaderWithType } from "../../../../../../../flight-shop/components/FlightShopHeader/FlightSummaryPanel/constants";
import { getStopsString } from "../../../../constants";

interface IFlightSummaryPanelProps {
  departure: boolean;
  tripDetails: TripDetails;
  airports: { [key: string]: Airport };
}

export const FlightSummaryPanel = ({
  departure,
  tripDetails,
  airports,
}: IFlightSummaryPanelProps) => {
  const sliceIndex = getSliceIndex(departure, tripDetails);
  const tripSlice = tripDetails.slices[sliceIndex];
  // get all operating airlines & add tooltips because only logos will be shown
  const airlines: any[] = tripSlice.segmentDetails.map((sd) => ({
    ...sd.operatingAirline,
    tooltip: `1 or more segments operated by ${sd.operatingAirline.name}`,
  }));

  // put marketing airline as first without tooltip
  airlines.unshift(tripSlice.segmentDetails[0].marketingAirline);

  // remove duplicates
  const uniqSegmentAirlines = uniqBy(airlines, "code");

  const { type, description } = getReviewCardHeaderWithType(
    departure,
    airports[tripSlice.destinationCode]
      ? airports[tripSlice.destinationCode].cityName
      : tripSlice.destinationName,
    removeTimezone(tripSlice.departureTime)
  );

  return (
    <Box className="airline-details-with-title">
      <Typography className="card-header">
        <span className="bold">{type}</span>
        <SanitizedHtml
          as="span"
          html={description}
        />
      </Typography>
      <Box className="airline-details">
        <Box className="airline-details-left-container">
          <Typography variant="body1" className="flight-timespan">
            {`${dayjs(removeTimezone(tripSlice.departureTime)).format(
              "h:mm A"
            )} - ${dayjs(removeTimezone(tripSlice.arrivalTime)).format(
              "h:mm A"
            )}`}
          </Typography>
          <Box className="card-airline-container">
            {uniqSegmentAirlines.map((airline, index) => (
              <React.Fragment key={`airline-tooltip-${index}-${airline.name}`}>
                {/* Only operating airlines should have a tooltip */}
                <Tooltip
                  arrow
                  title={airline.tooltip}
                >
                  <span>
                    <AirlineIcon airlineCode={airline.code} />
                  </span>
                </Tooltip>
                {/* Only first airline (marketing) should show name */}
                {index === 0 && (
                  <Typography
                    variant="body2"
                  >
                    {airline.name}
                    {uniqSegmentAirlines.length > 1 ? <span className="airline-divider">/</span> : null}
                  </Typography>
                )}
              </React.Fragment>
            ))}
          </Box>
        </Box>
        <Box className="airline-details-right-container">
          <Typography variant="body1" className="flight-timespan">
            {formatInterval(
              dayjs(tripSlice.arrivalTime).diff(
                dayjs(tripSlice.departureTime),
                "minute",
                true
              )
            )}
          </Typography>
          <Typography variant="body2">
            {getStopsString(tripSlice.stops)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
